import { doc, getDoc, setDoc, updateDoc, deleteField, collection, addDoc, writeBatch, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import {
  DEFAULT_INDUSTRY_ID,
  buildDefaultLeadSourcesForIndustry,
  buildDefaultCostCategoriesForIndustry,
  buildSeedExtras,
  hiddenFeaturesForIndustry,
} from '../utils/industryRegistry';
import { DEFAULT_BILLING_PLAN_ID, DEFAULT_SUBSCRIPTION_STATUS } from '../utils/billingUtils';
import { ACCOUNT_STATUS } from '../utils/accountStatus';

const EXTRAS_FIELDS = ['jobTypes', 'customFields', 'pricingPresets', 'companyDocumentFields', 'costCategories', 'leadSources'];

function extrasDocRef(companyId) {
  return doc(db, 'companies', companyId, 'meta', 'extras');
}

// Reads the main company doc. Heavy fields (jobTypes, customFields,
// pricingPresets, companyDocumentFields) live in a subdoc and are loaded
// separately by getCompanyExtras to keep this fetch small.
export async function getCompany(companyId) {
  const companyRef = doc(db, 'companies', companyId);
  const companySnap = await getDoc(companyRef);
  if (companySnap.exists()) {
    const data = companySnap.data();
    const core = { id: companySnap.id };
    Object.keys(data).forEach(k => {
      if (!EXTRAS_FIELDS.includes(k)) core[k] = data[k];
    });
    return core;
  }
  return null;
}

// Loads the extras subdoc. If it doesn't exist yet (legacy company), reads the
// heavy fields from the main doc as a fallback and attempts to migrate them
// into the subdoc (owner-only — non-owners just get the fallback data without
// migrating, which is fine).
export async function getCompanyExtras(companyId) {
  const eRef = extrasDocRef(companyId);
  const eSnap = await getDoc(eRef);
  if (eSnap.exists()) {
    return eSnap.data();
  }
  const companyRef = doc(db, 'companies', companyId);
  const companySnap = await getDoc(companyRef);
  if (!companySnap.exists()) return {};
  const data = companySnap.data();
  const extras = {
    jobTypes: data.jobTypes || [],
    customFields: data.customFields || {},
    pricingPresets: data.pricingPresets || [],
    companyDocumentFields: data.companyDocumentFields || [],
    costCategories: data.costCategories || [],
    leadSources: data.leadSources || [],
  };
  try {
    const batch = writeBatch(db);
    batch.set(eRef, extras);
    const stripUpdate = {};
    EXTRAS_FIELDS.forEach(f => { if (f in data) stripUpdate[f] = deleteField(); });
    if (Object.keys(stripUpdate).length > 0) {
      batch.update(companyRef, stripUpdate);
    }
    await batch.commit();
  } catch (err) {
    // Non-owners can't write to companies/{id} or its subdocs. Migration will
    // happen the next time an owner loads the app. Fallback data is still good.
    console.warn('Could not migrate company extras (likely non-owner):', err.message);
  }
  return extras;
}

export async function createCompany(name, ownerUid) {
  const companyData = {
    name,
    ownerUid,
    createdAt: new Date(),
    pipeline: [],
    servicePipeline: [
      'Request Received',
      'Scheduled',
      'Parts Ordered',
      'In Progress',
      'Complete',
    ],
    automations: [],
    onboardingComplete: false,
  };
  const companyRef = await addDoc(collection(db, 'companies'), companyData);
  // No industry is asked for here, so the default industry's lists.
  const costCategories = buildDefaultCostCategoriesForIndustry(DEFAULT_INDUSTRY_ID);
  const leadSources = buildDefaultLeadSourcesForIndustry(DEFAULT_INDUSTRY_ID);
  await setDoc(extrasDocRef(companyRef.id), {
    jobTypes: [],
    customFields: {},
    pricingPresets: [],
    companyDocumentFields: [],
    costCategories,
    leadSources,
  });
  return { id: companyRef.id, ...companyData, jobTypes: [], customFields: {}, pricingPresets: [], companyDocumentFields: [], costCategories, leadSources };
}

// Self-serve signup (/signup) company creation. Differs from createCompany
// (the legacy /onboarding path, unchanged) in three ways: it stamps the
// industry, it initializes the billing fields to the trial defaults, and it
// seeds the full default extras up front (job types, cost categories, lead
// sources, custom fields) instead of relying on CompanyContext's read-time
// seeding — the new owner's first load should never race the seed writes.
//
// The extras seed is INDUSTRY-DRIVEN: whichever pack the owner's answer
// seeds (utils/industryRegistry.js) decides the pipelines, sources,
// categories, custom-field template and price book they start with —
// buildSeedExtras is that whole document. The pack also names the features
// the company starts with hidden, written as `featureVisibility` exactly the
// way Settings → Feature Visibility stores a hidden one — and only when there
// are any, so a pack that hides nothing (Window & Door) writes the same
// document it always did. firestore.rules lets the signup create carry it.
//
// The billing values here must stay EXACTLY in sync with the signup create
// rule in firestore.rules, which only permits a client-created company whose
// billing fields are these precise trial defaults. Everything beyond a trial
// (active/canceled status, Stripe ids, overrides) remains Cloud-Function-only.
//
// THE ACCOUNT GATE (2026-10-17): every company is born accountStatus
// 'pending' — the same rule demands exactly that value, so a client can ask to
// be let in but never let itself in. Approval (or an invite) flips it on the
// server; see utils/accountStatus.js.
//
// `signupAnswers` ({ industry, teamSize } — utils/signupOptions.js) is what the
// applicant told us, for the review screen. `industry` above is the registry
// vertical the company is SEEDED from, which is not always the same thing.
export async function createSignupCompany({ name, industry, ownerUid, signupAnswers = null }) {
  const hidden = hiddenFeaturesForIndustry(industry);
  const companyData = {
    name,
    industry,
    ...(signupAnswers ? { signupAnswers } : {}),
    ownerUid,
    createdAt: new Date(),
    pipeline: [],
    servicePipeline: [
      'Request Received',
      'Scheduled',
      'Parts Ordered',
      'In Progress',
      'Complete',
    ],
    automations: [],
    onboardingComplete: false,
    ...(hidden.length ? { featureVisibility: Object.fromEntries(hidden.map(key => [key, false])) } : {}),
    accountStatus: ACCOUNT_STATUS.PENDING,
    subscriptionStatus: DEFAULT_SUBSCRIPTION_STATUS,
    billingPlanId: DEFAULT_BILLING_PLAN_ID,
    billingOverrides: null,
    stripeCustomerId: null,
    stripeSubscriptionId: null,
    subscriptionCurrentPeriodEnd: null,
  };
  const companyRef = await addDoc(collection(db, 'companies'), companyData);
  const extras = buildSeedExtras(industry);
  await setDoc(extrasDocRef(companyRef.id), extras);
  return { id: companyRef.id, ...companyData, ...extras };
}

export async function updateJobTypes(companyId, jobTypes) {
  await setDoc(extrasDocRef(companyId), { jobTypes }, { merge: true });
}

export async function updatePipeline(companyId, pipeline) {
  const companyRef = doc(db, 'companies', companyId);
  await updateDoc(companyRef, { pipeline });
}

export async function updateServicePipeline(companyId, servicePipeline) {
  const companyRef = doc(db, 'companies', companyId);
  await updateDoc(companyRef, { servicePipeline });
}

export async function updateCustomFields(companyId, customFields) {
  await setDoc(extrasDocRef(companyId), { customFields }, { merge: true });
}

export async function updateCompanyDocumentFields(companyId, companyDocumentFields) {
  await setDoc(extrasDocRef(companyId), { companyDocumentFields }, { merge: true });
}

export async function updatePipelineCategories(companyId, pipelineCategories) {
  const companyRef = doc(db, 'companies', companyId);
  await updateDoc(companyRef, { pipelineCategories });
}

export async function updateDashboardPreferences(companyId, dashboardPreferences) {
  const companyRef = doc(db, 'companies', companyId);
  await updateDoc(companyRef, { dashboardPreferences });
}

export async function updateFeatureVisibility(companyId, featureVisibility) {
  const companyRef = doc(db, 'companies', companyId);
  await updateDoc(companyRef, { featureVisibility });
}

export async function updatePermissions(companyId, permissions) {
  const companyRef = doc(db, 'companies', companyId);
  await updateDoc(companyRef, { permissions });
}

export async function updatePricingPresets(companyId, pricingPresets) {
  await setDoc(extrasDocRef(companyId), { pricingPresets }, { merge: true });
}

export async function updateCostCategories(companyId, costCategories) {
  await setDoc(extrasDocRef(companyId), { costCategories }, { merge: true });
}

// General Info dropdown options for the Pricing tab's master sheet. A flat
// optionKey -> string[] map; an absent key means "use BoneFlow's shipped
// defaults", so a company that never customizes stores nothing at all.
// See markSheetUtils.js (COMPANY-CUSTOM OPTION LISTS) for the key derivation.
export async function updateSheetSpecOptions(companyId, sheetSpecOptions) {
  await setDoc(extrasDocRef(companyId), { sheetSpecOptions }, { merge: true });
}

// The manufacturer / product-line / product-type price book.
// { manufacturers: [...] } — see utils/priceBookUtils.js for the full shape.
// The manufacturers ARRAY is what makes deletion work: merge:true replaces
// arrays wholesale but deep-merges maps, so an id-keyed map would resurrect
// deleted manufacturers on the next write.
export async function updatePriceBook(companyId, priceBook) {
  await setDoc(extrasDocRef(companyId), { priceBook }, { merge: true });
}

export async function updateLeadSources(companyId, leadSources) {
  await setDoc(extrasDocRef(companyId), { leadSources }, { merge: true });
}

// Notification Routing (roadmap 5.45): who gets told about each event type and
// how. An event-keyed MAP — see src/utils/notificationRoutingUtils.js for the
// shape and the shipped defaults.
//
// merge:true deep-merges maps, so writing one event's entry leaves the others
// alone. That is the right behavior here (unlike priceBook, where the array
// shape exists precisely to make deletion work) because the event KEYS are a
// fixed vocabulary the app owns — a company never deletes one, it disables it.
export async function updateNotificationRouting(companyId, notificationRouting) {
  await setDoc(extrasDocRef(companyId), { notificationRouting }, { merge: true });
}

// QuickBooks cost-category → account mapping. A flat map from a BoneFlow cost
// category id (see costCategoriesUtils.js) to a QuickBooks Chart of Accounts
// account id: { [costCategoryId]: quickbooksAccountId }.
//
// It lives in meta/extras beside the other data-as-config lists (jobTypes,
// costCategories, leadSources, priceBook) because it is exactly that:
// per-company configuration the owner edits, not derived state. An ABSENT key
// means "not configured", which is why nothing seeds a default — a half-guessed
// account mapping would post real expenses to the wrong ledger line. A category
// with no entry here simply does not sync, and that is a normal state, not a
// failure: functions/quickbooks.js records it as `skipped_unmapped`.
//
// THE MAP IS THE AUTHORITY; the names beside it are a display cache. Storing
// ids alone would mean the Settings screen could not say which account a
// category is filed under without a live QuickBooks round trip on every render,
// so `quickbooksAccountNames` ({ [accountId]: name }) rides along purely so the
// UI can render the current state instantly. Nothing on the server reads it —
// if a name goes stale because the account was renamed in QuickBooks, opening
// the picker refreshes it, and the posting was never keyed on it anyway.
//
// PASS A WHOLE MAP, not one entry: the caller has the complete map in hand from
// CompanyContext, and merge:true would otherwise make an UNMAPPING impossible
// (a deleted key is not a written key). Callers therefore build the next map
// and hand it over whole.
export async function updateQuickBooksAccountMap(companyId, quickbooksAccountMap, quickbooksAccountNames) {
  await setDoc(extrasDocRef(companyId), {
    quickbooksAccountMap,
    ...(quickbooksAccountNames ? { quickbooksAccountNames } : {}),
  }, { merge: true });
}

// WHICH ACCOUNT EXPENSES ARE PAID FROM — the QuickBooks `Purchase.AccountRef`,
// which Intuit makes mandatory on every Expense (fault 2020 without it, even on
// a sparse update).
//
// The server auto-resolves an existing bank or credit card account and caches
// it, exactly as the refund path does, so a company that never opens this
// setting still gets its costs into the books. This is the owner's override of
// that default, stored beside the cost-category map because it is the same kind
// of decision: which real account in THEIR chart of accounts a posting belongs
// to. Nothing is ever created — the picker offers only accounts that already
// exist. Pass null to fall back to the auto-resolved account.
export async function updateQuickBooksExpensePaymentAccount(companyId, account) {
  await setDoc(extrasDocRef(companyId), {
    quickbooksExpensePaymentAccountId: account ? String(account.id) : null,
    quickbooksExpensePaymentAccountName: account ? (account.name || null) : null,
  }, { merge: true });
}

// WHICH QUICKBOOKS TAX CODE BoneFlow's tax is filed under, chosen by the owner
// from their real tax code list in Settings → Integrations.
//
// It lives HERE, in extras, rather than on companies/{id} beside the other
// quickbooks* fields, because it is the same KIND of thing as
// quickbooksAccountMap above: a mapping the company configures, not connection
// state the server discovered. The meta/extras rule already restricts writes to
// the owner, so it needs no callable and no new rule. The server-owned
// quickbooks* fields on the company doc stay exactly what Intuit told us.
//
// It does not affect the AMOUNT — QuickBooks takes BoneFlow's TotalTax
// verbatim; the code decides which tax agency the amount is REPORTED under,
// which is precisely why a human picks it. Pass null to clear the mapping,
// which returns invoices to the flag-the-discrepancy behaviour.
export async function updateQuickBooksTaxCode(companyId, taxCode) {
  await setDoc(extrasDocRef(companyId), {
    quickbooksTaxCodeId: taxCode ? String(taxCode.id) : null,
    quickbooksTaxCodeName: taxCode ? (taxCode.name || null) : null,
  }, { merge: true });
}

export async function updateSalesmanStagePermissions(companyId, salesmanStagePermissions) {
  const companyRef = doc(db, 'companies', companyId);
  await updateDoc(companyRef, { salesmanStagePermissions });
}

export async function getStageCount(companyId, stageName, collectionName) {
  const ref = collection(db, collectionName);
  const q = query(ref, where('companyId', '==', companyId), where('stage', '==', stageName));
  const snapshot = await getDocs(q);
  return snapshot.size;
}

export async function migrateStageRename(companyId, oldStage, newStage, collectionName) {
  const ref = collection(db, collectionName);
  const q = query(ref, where('companyId', '==', companyId));
  const snapshot = await getDocs(q);

  const toUpdate = [];
  snapshot.docs.forEach(docSnap => {
    const data = docSnap.data();
    const stageMatch = data.stage === oldStage;
    const historyMatch = data.stageHistory?.some(h => h.stage === oldStage);
    if (!stageMatch && !historyMatch) return;

    const updates = {};
    if (stageMatch) updates.stage = newStage;
    if (historyMatch) {
      updates.stageHistory = data.stageHistory.map(h =>
        h.stage === oldStage ? { ...h, stage: newStage } : h
      );
    }
    toUpdate.push({ ref: docSnap.ref, updates });
  });

  const BATCH_SIZE = 500;
  for (let i = 0; i < toUpdate.length; i += BATCH_SIZE) {
    const batch = writeBatch(db);
    toUpdate.slice(i, i + BATCH_SIZE).forEach(({ ref, updates }) => batch.update(ref, updates));
    await batch.commit();
  }

  return toUpdate.filter(u => u.updates.stage !== undefined).length;
}

export async function setCompanyMigrationFlag(companyId, key, value = true) {
  const companyRef = doc(db, 'companies', companyId);
  await updateDoc(companyRef, { [`companyMigrated.${key}`]: value });
}

export async function migrateStageDelete(companyId, fromStage, toStage, collectionName) {
  const ref = collection(db, collectionName);
  const q = query(ref, where('companyId', '==', companyId), where('stage', '==', fromStage));
  const snapshot = await getDocs(q);

  const docs = snapshot.docs;
  const BATCH_SIZE = 500;
  for (let i = 0; i < docs.length; i += BATCH_SIZE) {
    const batch = writeBatch(db);
    docs.slice(i, i + BATCH_SIZE).forEach(docSnap => {
      batch.update(docSnap.ref, { stage: toStage });
    });
    await batch.commit();
  }

  return docs.length;
}